import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faBug } from '@fortawesome/free-solid-svg-icons';
import Table from '../../components/Listing/Table';
import Button from '../../components/Form/Button';
import Modal from '../../components/Modal/Modal';
import { usePageTitle } from '../../layouts/PageTitleContext';
import { API_BASE_URL } from '../../api';

const TYPE_LABELS = {
    BUG: 'Bug',
    EVOLUTION: 'Évolution',
    GENERAL: 'Général',
    AUTRE: 'Autre',
};

const StatusBadge = ({ status }) => {
    const resolved = status === 'RESOLVED';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
        }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: resolved ? '#10b981' : '#2563eb', flexShrink: 0 }} />
            {resolved ? 'Résolu' : 'Ouvert'}
        </span>
    );
};

export default function BackofficeIssues() {
    const { setTitle, setTitleIcon, setTitleDescription } = usePageTitle();
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keyword, setKeyword] = useState('');
    const [selected, setSelected] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        return axios.get(`${API_BASE_URL}/api/v1/backoffice/issues`, { withCredentials: true })
            .then(({ data }) => setIssues(data))
            .catch(() => toast.error('Impossible de charger les issues.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        document.title = 'Backoffice — Issues';
        load();
    }, [load]);

    useEffect(() => {
        setTitle('Issues');
        setTitleDescription("Tickets envoyés depuis le formulaire \"Nous contacter\" de l'application");
        setTitleIcon('ti-bug');
        return () => { setTitleDescription(null); setTitleIcon(null); };
    }, [setTitle, setTitleDescription, setTitleIcon]);

    const toggleStatus = (row) => {
        const nextStatus = row.status === 'RESOLVED' ? 'OPEN' : 'RESOLVED';
        axios.patch(`${API_BASE_URL}/api/v1/backoffice/issues/${row.id}/status`, null, { params: { status: nextStatus }, withCredentials: true })
            .then(({ data }) => {
                setIssues(prev => prev.map(i => (i.id === row.id ? data : i)));
                setSelected(prev => (prev && prev.id === row.id ? data : prev));
            })
            .catch(() => toast.error('Une erreur est survenue.'));
    };

    const handleDelete = (row) => {
        axios.delete(`${API_BASE_URL}/api/v1/backoffice/issues/${row.id}`, { withCredentials: true })
            .then(() => {
                toast.success('Issue supprimée.');
                load();
            })
            .catch(() => toast.error('Une erreur est survenue.'));
    };

    const columns = [
        {
            accessor: 'status',
            Header: 'Statut',
            sortable: true,
            Cell: row => <StatusBadge status={row.status} />,
        },
        {
            accessor: 'type',
            Header: 'Type',
            sortable: true,
            Cell: row => TYPE_LABELS[row.type] || row.type,
        },
        {
            accessor: 'subject',
            Header: 'Sujet',
            sortable: true,
            Cell: row => <span style={{ fontWeight: 600 }}>{row.subject}</span>,
        },
        { accessor: 'pharmacyName', Header: 'Pharmacie', Cell: row => row.pharmacyName || '—' },
        {
            accessor: 'userEmail',
            Header: 'Utilisateur',
            Cell: row => (
                <span>
                    {row.userFirstName} {row.userLastName}
                    <br />
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{row.userEmail}</span>
                </span>
            ),
        },
        {
            accessor: 'createdAt',
            Header: 'Reçu le',
            sortable: true,
            Cell: row => new Date(row.createdAt).toLocaleString('fr-FR'),
        },
        {
            accessor: 'actions',
            Header: '',
            className: 'col-actions',
            Cell: row => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={() => setSelected(row)}>Voir l'issue</button>
                        </li>
                        <li>
                            <button className="dropdown-item" onClick={() => toggleStatus(row)}>
                                {row.status === 'RESOLVED' ? 'Marquer comme ouvert' : 'Marquer comme résolu'}
                            </button>
                        </li>
                        <li>
                            <Button
                                className="dropdown-item text-danger"
                                onConfirm={() => handleDelete(row)}
                            >
                                Supprimer
                            </Button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                data={issues}
                mode="client"
                loading={loading}
                keyword={keyword}
                setKeyword={setKeyword}
                searchPlaceholder="Rechercher une issue..."
                rowId="id"
                pageSize={20}
                emptyMessage="Aucune issue"
                onRowClick={(row) => setSelected(row)}
            />

            <Modal
                show={!!selected}
                onClose={() => setSelected(null)}
                title={selected?.subject}
                icon={faBug}
            >
                {selected && (
                    <div>
                        <p><strong>Statut :</strong> <StatusBadge status={selected.status} /></p>
                        <p><strong>Type :</strong> {TYPE_LABELS[selected.type] || selected.type}</p>
                        <p><strong>Pharmacie :</strong> {selected.pharmacyName || '—'}</p>
                        <p><strong>Utilisateur :</strong> {selected.userFirstName} {selected.userLastName} ({selected.userEmail})</p>
                        <p><strong>Reçu le :</strong> {new Date(selected.createdAt).toLocaleString('fr-FR')}</p>
                        <hr />
                        <p style={{ whiteSpace: 'pre-wrap' }}>{selected.message}</p>
                        <div className="d-flex justify-content-end">
                            <Button className="modal-btn-primary" onClick={() => toggleStatus(selected)}>
                                {selected.status === 'RESOLVED' ? 'Marquer comme ouvert' : 'Marquer comme résolu'}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
}
